/** Recover safe reads within a bounded deadline; mutations are never replayed. */
const RETRYABLE = new Set([408, 429, 502, 503, 504])

export async function readResponse(
  url: string,
  init: RequestInit = {},
  timeoutMs = 15_000,
): Promise<Response> {
  const signal = AbortSignal.any([
    AbortSignal.timeout(timeoutMs),
    ...(init.signal ? [init.signal] : []),
  ])
  for (let attempt = 0; ; attempt++) {
    let response: Response | undefined
    try {
      response = await fetch(url, { ...init, method: 'GET', signal })
      if (!RETRYABLE.has(response.status) || attempt === 2) return response
    } catch (error) {
      if (signal.aborted || attempt === 2) throw error
    }
    const retryAfter = Number(response?.headers.get('Retry-After'))
    await response?.body?.cancel()
    await readDelay(
      Math.min(3000, Math.max(250 * 2 ** attempt, retryAfter * 1000 || 0)),
      signal,
    )
  }
}

/** Make backoff cancellable so leaving a view stops its pending work. */
export function readDelay(
  ms: number,
  signal?: AbortSignal | null,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason)
    const finish = () => {
      signal?.removeEventListener('abort', abort)
      resolve()
    }
    const timer = setTimeout(finish, ms)
    const abort = () => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', abort)
      reject(signal?.reason)
    }
    signal?.addEventListener('abort', abort, { once: true })
  })
}
